/**
 * Seam — production adapter wiring the React-free asymmetric-margin
 * ports ({@link AsymMarginDeps}) to the live engine and the structure-creation
 * choreography. The GPU/engine is a SYSTEM BOUNDARY: this glue is verified by
 * the manual demo + the mandatory HITL human safety review for this slice,
 * not by unit tests — the unit-tested safety core consumes these
 * ports through deterministic fakes.
 *
 * The engine leaves two finishing steps to the caller: pre-expand the source
 * grid (`createAsymmetricExpanded`) so outward margins have room, and crop the
 * padded result to image bounds before its volume is read or it is persisted.
 * Both happen in the apply closure below. The shared overlay/release/create wiring
 * lives in {@link makeEngineDeps}; this file holds only the op-specific apply
 * closure and the undo-history label. The patient-direction → signed engine
 * axis mapping is enforced upstream (golden-tested per direction); here the
 * already-mm `negLpsMm = [Right, Anterior, Inferior]` and
 * `posLpsMm = [Left, Posterior, Superior]` triples are passed straight through
 * to the engine in that exact locked slot order — no remapping, no inference.
 */
import { useCallback } from 'react';
import { vec3 } from 'gl-matrix';
import { useRecoilValue } from 'recoil';
import type { Environment } from '~/engine/data/Environment';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { useApplyComputedGeometry } from '~/hooks/structure-set/useApplyComputedGeometry';
import { environmentState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    makeEngineDeps,
    requireSourceTexture,
    toComputedGeometry,
} from '../core';
import type { ComputedGeometry } from '../core';
import type { AsymMarginDeps, LpsMarginMm } from './pipeline';

/**
 * Returns a factory producing {@link AsymMarginDeps} bound to the engine
 * state. Render-time handles are captured because `getOrCreateDestination`
 * needs the live environment.
 */
export function useAsymMarginEngineDeps(): () => AsymMarginDeps {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const applyGeometry = useApplyComputedGeometry();

    return useCallback(
        (): AsymMarginDeps =>
            makeEngineDeps(env, structureSet, applyGeometry, {
                label: 'Asymmetric Margin',
                geometry: (env: Environment | null) => ({
                    async applyAsymmetricMargin(
                        sourceRoiNumber: number,
                        negLpsMm: LpsMarginMm,
                        posLpsMm: LpsMarginMm
                    ): Promise<ComputedGeometry> {
                        const src = requireSourceTexture(env, sourceRoiNumber);
                        // Pre-expand the source grid by the largest outward
                        // margin: the engine sizes its output to the input
                        // grid and never grows it, so an outward margin beyond
                        // the source's padding would otherwise be truncated.
                        // An inward-only margin grows nothing — apply on the
                        // borrowed source directly (a 0-expansion would be a
                        // full clone for identical geometry).
                        const maxOutwardMm = Math.max(
                            0,
                            negLpsMm[0],
                            negLpsMm[1],
                            negLpsMm[2],
                            posLpsMm[0],
                            posLpsMm[1],
                            posLpsMm[2]
                        );
                        const expanded =
                            maxOutwardMm > 0
                                ? SDFOperations.createAsymmetricExpanded(
                                      src,
                                      maxOutwardMm
                                  )
                                : undefined;
                        // Fixed slot order:
                        // negLps=[Right,Anterior,Inferior], posLps=[Left,
                        // Posterior,Superior] — passed through unchanged. The
                        // pre-expanded input is caller-owned scratch; free it
                        // once apply settles (never the borrowed source).
                        const result =
                            await SDFOperations.applyAsymmetricMargin(
                                expanded ?? src,
                                vec3.fromValues(
                                    negLpsMm[0],
                                    negLpsMm[1],
                                    negLpsMm[2]
                                ),
                                vec3.fromValues(
                                    posLpsMm[0],
                                    posLpsMm[1],
                                    posLpsMm[2]
                                )
                            ).finally(() => expanded?.delete());
                        // applyAsymmetricMargin returns on the padded grid;
                        // crop to the image before measuring volume or
                        // persisting so the reported delta matches the saved
                        // geometry.
                        result.cropToImageBounds();
                        return toComputedGeometry(result);
                    },
                }),
            }),
        [env, structureSet, applyGeometry]
    );
}
